import type { ReactElement, ReactNode } from 'react';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

import VistaprintAuth, { WebAuth } from '@vp/auth';
import { useIdentity as useAuthReactIdentity } from '@vp/auth-react';
import { invariant } from '@vp/digital-invariant-lib';
import { useLocale } from '@vp/digital-locale-lib';
import { AccessType, useRuntimeContext } from '../../environment-lib';
import getLibLogger from './getLibLogger';

const DEFAULT_AUTH_CONNECTION = 'VP-Customers';

const AuthContext = createContext<InstanceType<typeof WebAuth> | undefined>(undefined);

type AuthProviderProps = {
  readonly children: ReactNode;
  readonly directAccessClientId?: string;
  readonly directAccessConnection?: string;
};

function AuthProvider({
  children,
  directAccessClientId,
  directAccessConnection = DEFAULT_AUTH_CONNECTION,
}: AuthProviderProps): ReactElement {
  const { current: logger } = useRef(getLibLogger());
  const { culture } = useLocale();
  const [webAuth, setWebAuth] = useState<InstanceType<typeof WebAuth> | undefined>();
  const { accessType } = useRuntimeContext();

  const developmentMode = useMemo(() => {
    // default to `false` means the default website auth (prod and staging) will be used
    let developmentMode: boolean | Record<string, unknown> = false;

    if (accessType === AccessType.Direct) {
      invariant(directAccessClientId, 'directAccessClientId is required');

      developmentMode = {
        clientID: directAccessClientId,
      };
    }

    return developmentMode;
  }, [accessType, directAccessClientId]);

  const options = useMemo(() => {
    if (developmentMode) {
      return {
        // default the connection to VP-Customers
        connection: directAccessConnection,
        redirectAfterSignOutCallback: () =>
          typeof globalThis.window === 'undefined' ? '' : globalThis.window.location.href.toString(),
        requireSession: true,
      };
    }
    return { requireSession: true };
  }, [directAccessConnection, developmentMode]);

  //Here is where we initialize the auth library
  useEffect(() => {
    if (developmentMode) {
      logger.info('Auth library is in development mode', developmentMode);
    }

    VistaprintAuth.init(
      {
        culture,
        developmentMode,
        options,
      },
      () => {
        logger.info('Auth library is initialized');

        const auth = new (WebAuth as any)();
        setWebAuth(auth);
      },
    );
  }, [culture, developmentMode, logger, options]);

  return <AuthContext.Provider value={webAuth}>{children}</AuthContext.Provider>;
}

function useWebAuth(): InstanceType<typeof WebAuth> | undefined {
  return useContext(AuthContext);
}

function useIdentity(): ReturnType<typeof useAuthReactIdentity> {
  return useAuthReactIdentity();
}

export { AuthProvider, useIdentity, useWebAuth };
